import * as React from "react";

import type {
  Filter,
  FilterMatchMode,
} from "@/shared/components/filters/FilterSchema";
import {
  functionalUpdate,
  useTable,
  type Table,
  type Updater,
  type UseTableOptions,
} from "@/shared/foundation/primitives/Table";

import { ActiveFilters, Filters } from "./Filters";
import type {
  ActiveFilters as ActiveFiltersState,
  FilterMenuOutput,
} from "./Filters/FilterModel";
import { ResourceViewActions } from "./ResourceViewActions";
import { ResourceViewRoot } from "./ResourceViewRoot";
import { Selection } from "./Selection";

/**
 * Options for `useResourceView`. Extends `UseTableOptions` with the
 * filter-state seeds.
 */
export interface UseResourceViewOptions<TData> extends UseTableOptions<TData> {
  /**
   * Initial filter state seeded into the hook at mount. Captured once;
   * later changes to this prop are ignored — the hook owns filter state
   * from then on. Use this for restoring saved or URL-encoded filter sets
   * at initial render. Defaults to `{ type: "none" }`.
   */
  initialActiveFilters?: ActiveFiltersState;
  /**
   * Initial match mode used when transitioning into `"structured"` mode
   * from `"none"` or `"roboql"`. Captured once; defaults to `"AND"`. Not
   * preserved across mode switches: toggling into `"roboql"` and back
   * re-seeds from this value rather than remembering the user's prior
   * AND/OR choice.
   */
  initialMatchMode?: FilterMatchMode;
}

/**
 * Returned by `useResourceView`. The `table` property is the nested
 * `Table<TData>` instance from `useTable` — pass it to `<DataTable>` and
 * read table-side fields off it (sort, selection, pageSize, etc.).
 *
 * **Always destructure into specific fields.** Do not pass `view` itself
 * as a prop (`<Foo view={view} />`). The view bundles both layers;
 * threading it whole forces the receiving component to re-render on
 * changes from either layer, defeating the separation. The hook
 * intentionally does NOT memoize its return value for this reason —
 * each piece inside (`table`, mutators, state) has its own stable
 * identity, but the wrapper does not.
 */
export interface ResourceView<TData> {
  /** The underlying table instance. Pass `view.table` to `<DataTable>` and
   *  read it for table-side fields (`sort`, `selection`, `pageSize`, etc.).
   *  Identity is stable across filter-only changes — components depending
   *  on table state don't re-render when only filter state moves. */
  readonly table: Table<TData>;

  // ── Filter state ──
  /** Wire-shape filter state: `"none"`, `"structured"`, or `"roboql"`. */
  readonly filters: ActiveFiltersState;
  /** True when the user has filters actively applied */
  readonly hasActiveFilters: boolean;

  // ── Mutators ──
  /** Commit a single filter to state. Structured filters append into
   *  the structured bucket (transitioning from `"none"` or `"roboql"`
   *  seeds `matchMode` from `initialMatchMode`); a roboql commit
   *  replaces state wholesale. */
  readonly commit: (output: FilterMenuOutput) => void;
  /** Remove the structured filter at `index`. In `"roboql"` mode any
   *  call collapses state to `"none"` (the chip is single-valued).
   *  No-op in `"none"`. */
  readonly removeFilter: (index: number) => void;
  /** Replace the structured filter at `index` with `filter` (the
   *  chip-edit gesture). No-op outside `"structured"` mode or when
   *  `index` is out of range — the edit popover is only reachable
   *  from a rendered chip, so both are programmer-error guards. */
  readonly updateFilter: (index: number, filter: Filter) => void;
  /** Drop all filters; reset state to `"none"`. */
  readonly clearFilters: () => void;
  /** Replace filter state wholesale — the load seam for a saved View.
   *
   *  `initialActiveFilters` only seeds at mount, so applying a View to an
   *  already-mounted table needs a mutator. Unlike `commit` this replaces
   *  rather than merges: a View is a complete filter set. Mirrors a roboql
   *  `SORT BY` into the table sort on the same terms `commit` does. */
  readonly applyFilters: (next: ActiveFiltersState) => void;
  /** Update `filters.matchMode` when in `"structured"` mode. No-op
   *  otherwise — the AND/OR picker is unreachable through the UI
   *  outside structured mode (it only renders with 2+ chips). */
  readonly setMatchMode: (updater: Updater<FilterMatchMode>) => void;
}

const NONE_STATE: ActiveFiltersState = { type: "none" };

/**
 * Hook for a paginated, sortable, filterable resource view.
 */
export function useResourceView<TData>(
  opts: UseResourceViewOptions<TData>,
): ResourceView<TData> {
  const table = useTable<TData>(opts);

  // ── Mount snapshots ─────────────────────────────────────────────────
  const [initialActiveFilters] = React.useState<ActiveFiltersState>(
    () => opts.initialActiveFilters ?? NONE_STATE,
  );
  const [initialMatchMode] = React.useState<FilterMatchMode>(
    () => opts.initialMatchMode ?? "AND",
  );

  // ── Live state ──────────────────────────────────────────────────────
  const [filters, setFilters] =
    React.useState<ActiveFiltersState>(initialActiveFilters);

  // ── Mutators ────────────────────────────────────────────────────────
  const { resetCurrentPage, setSort, resetSort } = table;

  const commit = React.useCallback(
    (output: FilterMenuOutput) => {
      if (output.type === "structured") {
        setFilters((prev) =>
          prev.type === "structured"
            ? { ...prev, filters: [...prev.filters, output.filter] }
            : {
                type: "structured",
                filters: [output.filter],
                matchMode: initialMatchMode,
              },
        );
      } else {
        if (output.spec.sort_by) {
          // The new query carries a `SORT BY`; mirror it into the table.
          setSort({
            columnId: output.spec.sort_by,
            direction: output.spec.sort_direction === "DESC" ? "desc" : "asc",
          });
        } else if (filters.type === "roboql" && filters.spec.sort_by) {
          // The previous roboql mirrored a `SORT BY` into the table sort and
          // the new query drops it. Dropping `SORT BY` is the user intending to reset it.
          resetSort();
        }
        setFilters({
          type: "roboql",
          query: output.query,
          spec: output.spec,
        });
      }
      resetCurrentPage();
    },
    [filters, initialMatchMode, resetCurrentPage, setSort, resetSort],
  );

  const removeFilter = React.useCallback(
    (index: number) => {
      // A roboql chip is single-valued; its × clears state. When that
      // chip wrote a `SORT BY` into the table sort, drop the ordering too
      // so the now-unfiltered table goes back to its default sort.
      if (filters.type === "roboql" && filters.spec.sort_by) {
        resetSort();
      }
      setFilters((prev) => {
        if (prev.type === "structured") {
          const next = prev.filters.filter((_, i) => i !== index);
          return next.length === 0 ? NONE_STATE : { ...prev, filters: next };
        }
        if (prev.type === "roboql") {
          return NONE_STATE;
        }
        return prev;
      });
      resetCurrentPage();
    },
    [filters, resetCurrentPage, resetSort],
  );

  const updateFilter = React.useCallback(
    (index: number, filter: Filter) => {
      setFilters((prev) => {
        if (
          prev.type !== "structured" ||
          index < 0 ||
          index >= prev.filters.length
        ) {
          return prev;
        }
        const next = [...prev.filters];
        next[index] = filter;
        return { ...prev, filters: next };
      });
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const clearFilters = React.useCallback(() => {
    // A roboql `SORT BY` is mirrored into the table sort; clearing the
    // filter must also drop that ordering, otherwise the unfiltered table
    // keeps the removed query's sort.
    if (filters.type === "roboql" && filters.spec.sort_by) {
      resetSort();
    }
    setFilters(NONE_STATE);
    resetCurrentPage();
  }, [filters, resetCurrentPage, resetSort]);

  const applyFilters = React.useCallback(
    (next: ActiveFiltersState) => {
      // Two separate cases: an incoming `SORT BY` sets the sort, and an
      // outgoing one makes its absence a reset rather than a carry-over.
      if (next.type === "roboql" && next.spec.sort_by) {
        setSort({
          columnId: next.spec.sort_by,
          direction: next.spec.sort_direction === "DESC" ? "desc" : "asc",
        });
      } else if (filters.type === "roboql" && filters.spec.sort_by) {
        resetSort();
      }
      setFilters(next);
      resetCurrentPage();
    },
    [filters, resetCurrentPage, setSort, resetSort],
  );

  const setMatchMode = React.useCallback(
    (updater: Updater<FilterMatchMode>) => {
      setFilters((prev) => {
        if (prev.type !== "structured") {
          // No-op outside structured mode — the picker is unreachable
          // through the UI in `"none"` / `"roboql"`. Returning `prev`
          // preserves identity so React skips the re-render.
          return prev;
        }
        const next = functionalUpdate(updater, prev.matchMode);
        if (next === prev.matchMode) {
          return prev;
        }
        return { ...prev, matchMode: next };
      });
      resetCurrentPage();
    },
    [resetCurrentPage],
  );

  const hasActiveFilters =
    filters.type === "roboql" ||
    (filters.type === "structured" && filters.filters.length > 0);

  // ── Instance ────────────────────────────────────────────────────────
  // Return a plain object literal — no useMemo wrapper. The view is meant
  // to be destructured by consumers; its own identity is irrelevant
  // because nothing should read it wholesale.
  //
  // ANTI-PATTERN: `<SomeComponent view={view} />`.
  //
  // The README (./README.md, "Anti-pattern: passing `view` wholesale")
  // documents this guarantee externally. If a future refactor wraps the
  // return value in `useMemo`, update the README in lockstep — the
  // "non-memoized wrapper" rationale there goes stale silently otherwise.
  return {
    table,
    filters,
    hasActiveFilters,
    commit,
    removeFilter,
    updateFilter,
    clearFilters,
    applyFilters,
    setMatchMode,
  };
}

/**
 * Compound namespace bundling the data-coupled UI affordances and layout
 * shell. Consumers compose them around `<DataTable>` (a foundation
 * primitive, imported directly):
 *
 * @example
 * ```tsx
 * <ResourceView.Root>
 *   <ResourceView.Actions>
 *     <ResourceView.Filters
 *       filterDefinitions={FIELDS}
 *       onFilterSelected={view.commit}
 *     />
 *     <ColumnVisibility ... />
 *     <ResourceView.Selection ... />
 *   </ResourceView.Actions>
 *   <ResourceView.ActiveFilters ... />
 *   <DataTable table={view.table} ... />
 * </ResourceView.Root>
 * ```
 */
export const ResourceView = {
  Root: ResourceViewRoot,
  Actions: ResourceViewActions,
  Filters,
  ActiveFilters,
  Selection,
};
